import type { KeyboardEvent, ReactNode } from "react";
import { cx, radius as radiusVar, space, type RadiusToken, type SpaceToken } from "./tokens.ts";
import type { Align } from "./types.ts";

export type CardProps = {
  /** Inner padding (default "4"). */
  padding?: SpaceToken;
  gap?: SpaceToken;
  /** Corner radius (default "lg"). */
  radius?: RadiusToken;
  direction?: "vertical" | "horizontal";
  /** Cross-axis alignment, as `Stack` (default stretch). */
  align?: Align;
  /**
   * "flat" (default): bg.surface with a border.subtle outline. "raised": bg.elevated + outline + shadow.sm (cards that
   * sit on the canvas and move, e.g. board cards; the outline keeps an edge in dark mode).
   */
  elevation?: "flat" | "raised";
  grow?: boolean;
  /** The whole card is one button (board card, file tile). Inner buttons keep their own presses. */
  onPress?: () => void;
  /** Name of a pressable card (default: its text content). */
  accessibilityLabel?: string;
  testID?: string;
  children?: ReactNode;
};

const alignMap = { start: "flex-start", center: "center", end: "flex-end", stretch: "stretch" } as const;

/** A grouped block of content on its own surface: settings groups, file details, board cards, empty states. */
export function Card({ padding = "4", gap, radius = "lg", direction, align, elevation = "flat", grow, onPress, accessibilityLabel, testID, children }: CardProps) {
  const pressable = onPress !== undefined;
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    onPress?.();
  };
  return (
    <div
      className={cx("b-card", `b-card-${elevation}`, direction === "horizontal" && "b-card-h", grow === true && "b-grow", pressable && "b-card-pressable")}
      role={pressable ? "button" : undefined}
      tabIndex={pressable ? 0 : undefined}
      aria-label={pressable ? accessibilityLabel : undefined}
      onClick={pressable ? (e) => {
        e.stopPropagation();
        onPress();
      } : undefined}
      onKeyDown={pressable ? onKeyDown : undefined}
      data-testid={testID}
      style={{
        padding: space(padding),
        gap: gap === undefined ? undefined : space(gap),
        borderRadius: radiusVar(radius),
        alignItems: align === undefined ? undefined : alignMap[align],
      }}
    >
      {children}
    </div>
  );
}
